import { useEffect, useReducer, useRef } from 'react'
import * as React from 'react'
import type { DevPanelAction, DevPanelData } from './types'
import { DevPanelDispatchContext, DevPanelOpenContext } from './DevPanelContext'
import useToggle from 'hooks/useToggle'
import DevPanelRoot from './DevPanelRoot'
import DevCustomerProfileRestore from './DevCustomerProfileRestore'
import config from 'constants/config'
import loadable from '@loadable/component'
const DevPanelWindow = loadable(() => import('./DevPanelWindow'))

const STORAGE_KEY = 'DevPanel:isOpen'

const initialValue: DevPanelData = {
  contexts: [],
}

function reducer(state: DevPanelData, action: DevPanelAction): DevPanelData {
  switch (action.type) {
    case 'reportContext':
      const contexts = [...state.contexts]
      const index = contexts.findIndex(ctx => ctx.id === action.context.id)
      if (index === -1) {
        contexts.push(action.context)
      } else {
        contexts[index] = action.context
      }
      return {
        ...state,
        contexts,
      }
    case 'removeContext':
      return {
        ...state,
        contexts: state.contexts.filter(ctx => ctx.id !== action.id),
      }
    default:
      return state
  }
}

function DevPanelProvider({ children }: React.PropsWithChildren) {
  const { value: isOpen, on: openWindow, off: closeWindow } = useToggle()
  const [state, dispatch] = useReducer<DevPanelData, [DevPanelAction]>(reducer, initialValue)
  const devWindowRef = useRef<WindowProxy | null>(null)
  const unloading = useRef(false)

  // Auto-open on reload if previously open this session
  useEffect(() => {
    try {
      if (config.ENABLE_DEV_PANEL && sessionStorage.getItem(STORAGE_KEY) === '1') {
        openWindow()
      }
    } catch {}
  }, [openWindow])

  // Persist open state for auto-reconnect
  useEffect(() => {
    if (config.ENABLE_DEV_PANEL && !unloading.current) {
      try {
        sessionStorage.setItem(STORAGE_KEY, isOpen ? '1' : '0')
      } catch {}
    }
  }, [isOpen])

  useEffect(() => {
    if (config.ENABLE_DEV_PANEL) {
      const handleKeyDown = (event: KeyboardEvent) => {
      // Check for cmd+alt+shift+d (Mac) or ctrl+alt+shift+d (Windows/Linux)
        if (
          (event.metaKey || event.ctrlKey) &&
          event.altKey &&
          event.shiftKey &&
          event.code === 'KeyD'
        ) {
          event.preventDefault()
          if (isOpen && devWindowRef.current && !devWindowRef.current.closed) {
            devWindowRef.current.focus()
          } else {
            if (isOpen) closeWindow()
            openWindow()
          }
        }
      }

      document.addEventListener('keydown', handleKeyDown)

      return () => {
        document.removeEventListener('keydown', handleKeyDown)
      }
    }
  }, [isOpen, openWindow, closeWindow])

  useEffect(() => {
    function onUnload() {
      unloading.current = true
    }

    window.addEventListener('beforeunload', onUnload)

    return () => {
      window.removeEventListener('beforeunload', onUnload)
    }
  }, [])

  if (!config.ENABLE_DEV_PANEL) {
    return <>
      {children}
    </>
  }

  return (
    <DevPanelOpenContext value={openWindow}>
      <DevPanelDispatchContext value={dispatch}>
        <DevCustomerProfileRestore />
        {isOpen && <DevPanelWindow onClose={closeWindow} ref={devWindowRef}>
          <DevPanelRoot state={state} />
        </DevPanelWindow>}
        {children}
      </DevPanelDispatchContext>
    </DevPanelOpenContext>
  )
}

export default DevPanelProvider
